import type { StatisticsResponse } from "@harmonyso/conversations-viewer-sdk";
import { findCell, type Cell } from "./cells";
import type { CellFailure } from "./cell-tenants";

/**
 * Per-call options that send a conversations-viewer request to the given
 * cell. The primary cell gets no override, so its requests and query keys are
 * what they were before cells existed. Another cell gets a timeout, so a cell
 * that hangs can't hold the page on "Loading".
 */
export function conversationsViewerCellOptions(
  cellId: string | null | undefined,
): { baseURL?: string; timeout?: number } {
  const remote = findCell(cellId)?.remote;
  return remote
    ? { baseURL: `${remote.apiBaseUrl}/conversations-viewer`, timeout: 15_000 }
    : {};
}

/**
 * The tenants to leave out of one cell's statistics: the always-excluded
 * internal tenants plus every tenant whose home is another cell. A cell can
 * hold copies of another cell's conversations, so each tenant is counted only
 * in its own cell.
 *
 * The ids go in the query string, about 56 bytes each, so a cell's request
 * grows with the other cells' tenant counts (about 8 KB for 140 tenants).
 * Envoy's 60 KiB header limit allows roughly 1,000; an edge proxy in front of
 * a cell (Cloudflare about 16 KB, CloudFront 8 KB) would allow far fewer and
 * would need a server-side cell filter instead.
 */
export function homeCellExclusions(
  cellId: string,
  tenants: { id: string; deployment_cell?: string | null }[],
  alwaysExcluded: string[],
): string[] {
  const elsewhere = tenants
    .filter((tenant) => tenant.deployment_cell !== cellId)
    .map((tenant) => tenant.id);
  return [...new Set([...alwaysExcluded, ...elsewhere])].sort();
}

export interface CellStatisticsResult {
  cell: Cell;
  stats?: StatisticsResponse;
  error?: Error | null;
  isLoading?: boolean;
}

export interface MergedCellStatistics {
  /** Every loaded cell's counts summed; undefined until a cell has loaded. */
  stats: StatisticsResponse | undefined;
  /** Each loaded cell's total, in cell order. */
  perCell: { cell: Cell; total: number }[];
  /** The cell each tenant in `tenant_distribution` was counted in. */
  tenantCellIds: Map<string, string>;
  failures: CellFailure[];
  loadingCells: Cell[];
}

type Distribution = Record<string, number>;

class DistributionSum {
  private readonly counts = new Map<string, number>();

  add(distribution: Distribution | null | undefined): void {
    for (const [key, count] of Object.entries(distribution ?? {})) {
      this.counts.set(key, (this.counts.get(key) ?? 0) + count);
    }
  }

  toObject(): Distribution {
    return Object.fromEntries(this.counts);
  }
}

/**
 * Sums the statistics of every cell that answered. Every field is a count,
 * so totals and distributions add up; percentages are left to the caller.
 * `tenant_distribution` stays null when no cell returned one (a tenant
 * filter), so "not computed" isn't shown as "no tenants".
 */
export function mergeCellStatistics(
  results: CellStatisticsResult[],
): MergedCellStatistics {
  const failures: CellFailure[] = [];
  const loadingCells: Cell[] = [];
  const perCell: MergedCellStatistics["perCell"] = [];
  const tenantCellIds = new Map<string, string>();
  const sums = {
    category: new DistributionSum(),
    sentiment: new DistributionSum(),
    workflowType: new DistributionSum(),
    app: new DistributionSum(),
    tenant: new DistributionSum(),
    daily: new DistributionSum(),
  };
  let total = 0;
  let hasTenantDistribution = false;

  for (const { cell, stats, error, isLoading } of results) {
    if (error) {
      failures.push({ cell, error });
      continue;
    }
    if (!stats) {
      if (isLoading) {
        loadingCells.push(cell);
      }
      continue;
    }

    perCell.push({ cell, total: stats.total_conversations });
    total += stats.total_conversations;
    sums.category.add(stats.category_distribution);
    sums.sentiment.add(stats.sentiment_distribution);
    sums.workflowType.add(stats.workflow_type_distribution);
    sums.app.add(stats.app_distribution);
    sums.daily.add(stats.daily_distribution);
    if (stats.tenant_distribution) {
      hasTenantDistribution = true;
      sums.tenant.add(stats.tenant_distribution);
      for (const tenantId of Object.keys(stats.tenant_distribution)) {
        tenantCellIds.set(tenantId, cell.id);
      }
    }
  }

  const stats: StatisticsResponse | undefined =
    perCell.length === 0
      ? undefined
      : {
          total_conversations: total,
          category_distribution: sums.category.toObject(),
          sentiment_distribution: sums.sentiment.toObject(),
          workflow_type_distribution: sums.workflowType.toObject(),
          app_distribution: sums.app.toObject(),
          tenant_distribution: hasTenantDistribution
            ? sums.tenant.toObject()
            : null,
          daily_distribution: sums.daily.toObject(),
        };

  return { stats, perCell, tenantCellIds, failures, loadingCells };
}

/**
 * The warning heading for a cell that didn't load, naming the cells the
 * numbers on the page do cover.
 */
export function cellNotLoadedHeading(cell: Cell, loadedCells: Cell[]): string {
  if (loadedCells.length === 0) {
    return `${cell.residency} did not load - no statistics shown`;
  }
  return `${cell.residency} did not load - showing ${loadedCells
    .map(({ residency }) => residency)
    .join(" + ")} only`;
}
